import React, { useState, useRef, useEffect } from 'react';
import { useCurrentFacility } from '../helpers/acl/facility';

/**
 * Left-bar scope selector. Shown only when the user has more than one place to
 * switch between (facilities + parent org nodes they hold a role on). Picking a
 * facility narrows every page (patients / exams / kpis ...) to it; picking a
 * parent org node clears that filter and shows the user's full ACL scope.
 */
const FacilitySelector: React.FC<{ menuOpen?: boolean }> = ({ menuOpen = true }) => {
  const { facilities, orgNodes, current, currentOrg, isMulti, selectFacility, selectOrg } =
    useCurrentFacility();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!isMulti) return null;

  const label = currentOrg?.name || current?.name || 'Select scope';

  return (
    <div className="relative mb-2" ref={ref}>
      <div
        onClick={() => setOpen(!open)}
        title={label}
        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
          !menuOpen && 'justify-center'
        }`}
      >
        <i
          className={`${
            currentOrg ? 'ri-government-line' : 'ri-building-line'
          } text-lg text-gray-600 dark:text-gray-300 flex-shrink-0`}
        />
        {menuOpen && (
          <div className="flex-1 overflow-hidden flex items-center justify-between">
            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{label}</p>
            <i
              className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-sm text-gray-500 dark:text-gray-400`}
            />
          </div>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[12rem] z-50 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
          {orgNodes.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                selectOrg(n.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                currentOrg?.id === n.id
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              <i className="ri-government-line text-base flex-shrink-0" />
              <span className="truncate flex-1">{n.name}</span>
              <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 flex-shrink-0">
                {n.type}
              </span>
            </button>
          ))}

          {orgNodes.length > 0 && facilities.length > 0 && (
            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
          )}

          {facilities.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                selectFacility(f.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                current?.id === f.id
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              <i className="ri-building-line text-base flex-shrink-0" />
              <span className="truncate">{f.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FacilitySelector;
